import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { ParseConfig } from './Step2ParseConfig'
import { RetrievalConfig } from './Step3RetrievalEnhancement'

interface Step5ReviewProps {
  name: string
  description: string
  parseConfig: ParseConfig
  retrievalConfig: RetrievalConfig
}

const CHUNK_METHOD_LABELS: Record<string, string> = {
  naive: 'General',
  book: 'Book',
  manual: 'Manual',
  qa: 'Q&A',
  table: 'Table',
  paper: 'Paper',
  laws: 'Laws',
  presentation: 'Presentation',
  picture: 'Picture',
  one: 'One',
  email: 'Email',
}

export function Step5Review({
  name,
  description,
  parseConfig,
  retrievalConfig,
}: Step5ReviewProps) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-1">Review & Create</h3>
        <p className="text-sm text-muted-foreground">
          Review your knowledge base configuration before creating. You can go back to any step to make changes.
        </p>
      </div>

      {/* Basic Information */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Basic Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Name</span>
            <span className="font-medium">{name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Description</span>
            <span className="font-medium text-right max-w-[60%] truncate">
              {description || 'No description'}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Parse Configuration */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Parse Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Chunking Method</span>
            <span className="font-medium">
              {CHUNK_METHOD_LABELS[parseConfig.chunkMethod] || parseConfig.chunkMethod}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Embedding Model</span>
            <span className="font-medium">text-embedding-3-large (OpenAI)</span>
          </div>
          {parseConfig.chunkMethod === 'naive' && (
            <>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Chunk Size</span>
                <span className="font-medium">{parseConfig.chunkTokenNum} tokens</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Delimiter</span>
                <span className="font-medium font-mono">{parseConfig.delimiter}</span>
              </div>
            </>
          )}
          <div className="flex justify-between">
            <span className="text-muted-foreground">Layout Recognition</span>
            <span className="font-medium">{parseConfig.layoutRecognize}</span>
          </div>
        </CardContent>
      </Card>

      {/* Retrieval Enhancement */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Retrieval Enhancement</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Page Rank</span>
            <span className="font-medium">{retrievalConfig.pagerank}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Auto Keywords</span>
            <span className="font-medium">
              {retrievalConfig.autoKeywords === 0
                ? 'Disabled'
                : `${retrievalConfig.autoKeywords} per chunk`}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Auto Questions</span>
            <span className="font-medium">
              {retrievalConfig.autoQuestions === 0
                ? 'Disabled'
                : `${retrievalConfig.autoQuestions} per chunk`}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Advanced Features */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Advanced Features</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Tagging, RAPTOR, and Knowledge Graph features are not yet configured. You can enable these later from the knowledge base settings.
        </CardContent>
      </Card>
    </div>
  )
}
